/**
 * AllJournalArticlesPage
 *
 * The default journal articles view: every article in the bibliographic
 * sources database, with the standard table (which includes its own toolbar and
 * pagination). Sorting is disabled for this view in the context (it times out on
 * the full dataset).
 */

import { useMemo } from "react"
import JournalArticlesShell from "@/components/journal-articles/JournalArticlesShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  useJournalArticlesPage,
  stripLocationColumns,
} from "@/components/journal-articles/useJournalArticlesPage"

/** Browser tab title and page heading. */
const PAGE_TITLE: string = "All Journal Articles"

/** Short description shown under the page heading. */
const PAGE_DESCRIPTION: string = "Browse journal articles from the bibliographic sources database"

/**
 * The All Journal Articles page.
 *
 * @returns The rendered page element.
 */
export default function AllJournalArticlesPage(): React.JSX.Element {
  const page = useJournalArticlesPage({ sidebarOption: "all-journal-articles" })

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  // Location columns only apply to the Open Author Contacts page; strip them here.
  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  return (
    <JournalArticlesShell
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      <JournalArticlesTable page={page} filterOptions={filterOptions} visibleColumns={visibleColumns} />
    </JournalArticlesShell>
  )
}
